// resumoInsights.js
// Versão analítica “mestre de processos”: KPIs, riscos, carga de operadores e datasets de gráficos

// Import opcional: se não houver config, usamos defaults robustos
import { getJSON } from "../services/api";

const DEFAULT_CFG = {
  pesos: { viola: 10, sobre: 3, sub: 4, drift: 6, tol: 1, vol: 0.5, volumeGapDias: 8 },
  dma: { mode: "mixed", insideWeight: 0.4, outsideWeight: 1.0 },
  metas: { leiturasPorDia: 100 },
  sobrecarga: { maxBicos: 6, maxShare: 60 },
  multiplicadorDias: { modo: "max" },
};

export async function loadConfigAnalise() {
  try {
    const data = await getJSON("/config/analise");
    if (!data) return DEFAULT_CFG;

    return {
      pesos: { ...DEFAULT_CFG.pesos, ...(data.pesos || {}) },
      metas: { ...DEFAULT_CFG.metas, ...(data.metas || {}) },
      sobrecarga: { ...DEFAULT_CFG.sobrecarga, ...(data.sobrecarga || {}) },
      multiplicadorDias: { ...DEFAULT_CFG.multiplicadorDias, ...(data.multiplicadorDias || {}) },
      dma: { ...DEFAULT_CFG.dma, ...(data.dma || {}) },
    };
  } catch (e) {
    console.error("Erro ao carregar config de análise:", e);
    return DEFAULT_CFG;
  }
}

const PESOS_LABELS = {
  viola: 'Violação (fora da especificação)',
  sobre: 'Acima do alvo',
  sub: 'Abaixo do alvo',
  drift: 'Fora da faixa ideal',
  tol: 'Em tolerância',
  vol: 'Volume (meta)',
  volumeGapDias: 'Gap de volume'
};

// Helpers
const to1 = (n) => Number.isFinite(n) ? Number(n.toFixed(1)) : 0;
const to2 = (n) => Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
const pct = (x) => Number.isFinite(x) ? Number(x) : 0; // seus campos já vêm em %

function mid(a, b) {
  const A = Number(a), B = Number(b);
  return (Number.isFinite(A) && Number.isFinite(B)) ? (A + B) / 2 : NaN;
}

function clampToIdeal(d) {
  const i = Number(d.params?.ideal_i), f = Number(d.params?.ideal_f);
  if (!Number.isFinite(i) || !Number.isFinite(f)) return { i: NaN, f: NaN, c: NaN };
  return { i, f, c: (i + f) / 2 };
}

function distToNearestLimit(x, i, f) {
  if (!Number.isFinite(x) || !Number.isFinite(i) || !Number.isFinite(f)) return NaN;
  if (x < i) return i - x;
  if (x > f) return x - f;
  return 0; // dentro da faixa ideal
}

function dmaCentroBico(d) {
  const { c } = clampToIdeal(d);
  if (!Number.isFinite(c)) return NaN;
  return Math.abs(Number(d.media) - c);
}

function dmaLimiteBico(d) {
  const { i, f } = clampToIdeal(d);
  return distToNearestLimit(Number(d.media), i, f);
}

function dmaMistoBico(d, insideW=0.4, outsideW=1.0) {
  const { i, f, c } = clampToIdeal(d);
  if (!Number.isFinite(i) || !Number.isFinite(f) || !Number.isFinite(c)) return NaN;
  const m = Number(d.media);
  const inside = (m >= i && m <= f);
  const dist = inside ? Math.abs(m - c) : distToNearestLimit(m, i, f);
  const w = inside ? insideW : outsideW;
  return dist * w;
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function nomesFromColab(colaboradores) {
  return String(colaboradores || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function scoreDeBico(d, pesos, metas) {
  // Seus campos acima/abaixo/tolerancia/ideais são % (0–100)
  const viola = (d.media < d.params.min || d.media > d.params.max) ? pesos.viola : 0;
  const drift = (d.media < d.params.ideal_i || d.media > d.params.ideal_f) ? pesos.drift : 0;
  const sobre = pct(d.acima) * pesos.sobre;
  const sub   = pct(d.abaixo) * pesos.sub;
  const tol   = pct(d.tolerancia) * pesos.tol;

  const volFactor = (Number(d.total) / Number(metas.leiturasPorDia || 1));
  const vol = volFactor * pesos.vol;
  const volumeGap = Number(d.total) < Number(metas.leiturasPorDia) ? pesos.volumeGapDias : 0;

  return viola + drift + sobre + sub + tol + vol + volumeGap;
}

function dmaBico(d) {
  // desvio médio absoluto do centro ideal
  const c = mid(d.params?.ideal_i, d.params?.ideal_f);
  if (!Number.isFinite(c)) return NaN;
  return Math.abs(Number(d.media) - c);
}

export function analyzeResumo(input, cfgOverride = {}, cfgBase = null) {

  const baseCfg = {
    pesos: { ...DEFAULT_CFG.pesos, ...(cfgBase?.pesos || {}) },
    metas: { ...DEFAULT_CFG.metas, ...(cfgBase?.metas || {}) },
    sobrecarga: { ...DEFAULT_CFG.sobrecarga, ...(cfgBase?.sobrecarga || {}) },
    multiplicadorDias: { ...DEFAULT_CFG.multiplicadorDias, ...(cfgBase?.multiplicadorDias || {}) },
    dma: { ...DEFAULT_CFG.dma, ...(cfgBase?.dma || {}) },
  };

  const env = safeArray(input?.envasadoras ?? input);
  const operadoresInfo = safeArray(input?.operadores);

  // helper para chave estável (preferir matrícula)
  const normStr = s => String(s||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/\s+/g,' ').trim().toUpperCase();
  const opKey = (o) => (o?.matricula != null ? String(o.matricula)
                       : o?.id != null ? String(o.id)
                       : normStr(o?.nome));

  // catálogo para display
  const opMetaByKey = new Map();
  operadoresInfo.forEach(o => {
    const key = (o?.matricula != null ? String(o.matricula)
              : o?.id != null ? String(o.id)
              : normStr(o?.nome));
    if (!key) return;
    opMetaByKey.set(key, {
      nome: o?.nome || key,
      matricula: (o?.matricula != null ? String(o.matricula) : null),
      id: (o?.id != null ? String(o.id) : null),
    });
  });

  // estatísticas base (da consulta de operadores)
  const opsStats = {};
  operadoresInfo.forEach(o => {
    const key = opKey(o);
    if (!key) return;
    const bicosArr = Array.isArray(o?.bicos) ? o.bicos.map(String) : [];
    opsStats[key] = {
      bicos: new Set(bicosArr),
      presencas: Number(o?.dias_trabalhados || 0),
      totalLeituras: Number(o?.leituras || 0),
    };
  });

  // merge com dados de envasadoras (consulta por bico)
  env.forEach(d => {
    const total = Number(d?.total || 0);
    const bico  = String(d?.bico ?? '');
    const matriculas = Array.isArray(d?.matriculasArr)
      ? d.matriculasArr
      : String(d?.matriculas || '').split(',').map(x=>x.trim()).filter(Boolean);

    matriculas.forEach(m => {
      const key = String(m).trim();
      if (!key) return;
      if (!opsStats[key]) opsStats[key] = { bicos: new Set(), presencas: 0, totalLeituras: 0 };
      opsStats[key].bicos.add(bico);
      opsStats[key].totalLeituras += total;
      // não cria opMetaByKey aqui se não tiver nome; o catálogo oficial vem do operadoresInfo
    });
  });

  const display = (key) => {
    const meta = opMetaByKey.get(String(key));
  if (!meta) return String(key); // fallback mostra só a matrícula
  return meta.matricula ? `${meta.matricula} - ${meta.nome}` : meta.nome;
  };
  

  if (env.length === 0) {
    return {
      // legado
      resumo: 'Nenhum dado para análise.',
      riscos: [],
      sobrecarga: [],
      desvios: [],
      // novo
      summary: [],
      kpis: {},
      risksTop: [],
      operatorLoad: [],
      operatorIssues: [],
      charts: {},
      meta: {}
    };
  }

  // Config com fallback seguro
  const dmaCfg = { ...baseCfg.dma, ...(cfgOverride?.dma || {}) };
  const iw = dmaCfg?.insideWeight ?? DEFAULT_CFG.dma.insideWeight;
  const ow = dmaCfg?.outsideWeight ?? DEFAULT_CFG.dma.outsideWeight;
  const { pesos, metas, sobrecarga, multiplicadorDias } = baseCfg;

  // ===== Score por bico =====
  const analise = env.map(d => {
    const baseScore = scoreDeBico(d, pesos, metas);

    // multiplicador por "dias trabalhados" dos operadores que tocaram o bico
    const ids = Array.isArray(d?.matriculasArr)
      ? d.matriculasArr.map(String)
      : String(d?.matriculas || '').split(',').map(s => s.trim()).filter(Boolean);

    const presencas = ids.map(id => opsStats[id]?.presencas || 0);

    let mult = 1;
    if (multiplicadorDias.modo === 'sum') mult = presencas.reduce((s, x) => s + x, 0) || 1;
    else if (multiplicadorDias.modo === 'avg') mult = presencas.reduce((s, x) => s + x, 0) / Math.max(presencas.length, 1) || 1;
    else mult = Math.max(...presencas, 1);

    const score = baseScore * mult;

    // >>> DMA: calcular *por bico* aqui dentro, usando iw/ow já definidos
    const dmaCentro = dmaCentroBico(d);
    const dmaLimite = dmaLimiteBico(d);
    const dmaMisto  = dmaMistoBico(d, iw, ow);
    const dmaUsed   = dmaCfg?.mode === 'center' ? dmaCentro
                    : dmaCfg?.mode === 'limit'  ? dmaLimite
                    :                             dmaMisto;

    // breakdown do score (antes do retorno)
    const violaPart = (d.media < d.params.min || d.media > d.params.max) ? pesos.viola : 0;
    const driftPart = (d.media < d.params.ideal_i || d.media > d.params.ideal_f) ? pesos.drift : 0;
    const sobrePart = pct(d.acima) * pesos.sobre;
    const subPart   = pct(d.abaixo) * pesos.sub;
    const tolPart   = pct(d.tolerancia) * pesos.tol;
    const volPart   = ((Number(d.total) / Number(metas.leiturasPorDia || 1)) * pesos.vol);
    const volumeGapPart = Number(d.total) < Number(metas.leiturasPorDia) ? pesos.volumeGapDias : 0;

    let dmaHint = '';
    if (dmaCfg.mode === 'center') {
      dmaHint = `Ref.: centro ideal ${dmaCentro} g`;
    } else if (dmaCfg.mode === 'limit') {
      dmaHint = `Ref.: limite ideal ${dmaLimite} g`;
    } else if (dmaCfg.mode === 'mixed') {
      dmaHint = `Pondera ${iw}× dentro / ${ow}× fora do ideal. Ref.: centro ${dmaCentro} g`;
}

    return {
      bico: d.bico,
      operadores: ids.map(display), // exibe "matrícula - nome"
      score: to2(score),
      media: Number(d.media),
      total: Number(d.total),
      abaixo: pct(d.abaixo)?.toFixed(2),
      acima: pct(d.acima)?.toFixed(2),
      tolerancia: pct(d.tolerancia)?.toFixed(2),
      ideais: pct(d.ideais),
      faixa: { min: Number(d.params?.min), max: Number(d.params?.max) },
      ideal: { i: Number(d.params?.ideal_i), f: Number(d.params?.ideal_f) },
      dma: Number(dmaUsed?.toFixed(2)),
      dmaHint,
      dmaCentro: Number(dmaCentro?.toFixed(2)),
      dmaLimite: Number(dmaLimite?.toFixed(2)),
      dmaMisto:  Number(dmaMisto?.toFixed(2)),
      violacao: (d.media < d.params.min || d.media > d.params.max) ? 1 : 0,
      drift: (d.media < d.params.ideal_i || d.media > d.params.ideal_f) ? 1 : 0,
      breakdown: {
        viola: (d.media < d.params.min || d.media > d.params.max) ? pesos.viola : 0,
        drift: (d.media < d.params.ideal_i || d.media > d.params.ideal_f) ? pesos.drift : 0,
        sobre: pct(d.acima) * pesos.sobre,
        sub:   pct(d.abaixo) * pesos.sub,
        tol:   pct(d.tolerancia) * pesos.tol,
        vol:   ((Number(d.total) / Number(metas.leiturasPorDia || 1)) * pesos.vol)?.toFixed(2),
        volumeGap: Number(d.total) < Number(metas.leiturasPorDia) ? pesos.volumeGapDias : 0,
        mult: to2(mult),
      },
    };
  }).sort((a, b) => b.score - a.score);

  // ===== KPIs globais =====
  const totalLeituras = env.reduce((s, d) => s + Number(d.total || 0), 0);
  const w = (d) => Number(d.total || 0) / Math.max(totalLeituras, 1); // peso por volume

  const conformidadeIdeal = env.reduce((s, d) => s + w(d) * pct(d.ideais), 0);        // %
  const conformidadeTolerada = env.reduce((s, d) => s + w(d) * pct(d.tolerancia), 0);  // %
  const taxaViolacoes = env.reduce((s, d) => s + w(d) * (pct(d.acima) + pct(d.abaixo)), 0); // %
  const dmaGlobal = env.reduce((s, dItem) => {
    const vc = dmaCentroBico(dItem);
    const vl = dmaLimiteBico(dItem);
    const vm = dmaMistoBico(dItem, iw, ow);
    const v  = dmaCfg?.mode === 'center' ? vc : dmaCfg?.mode === 'limit' ? vl : vm;
    return s + w(dItem) * (Number.isFinite(v) ? v : 0);
  }, 0);

  // comparativos (independem do modo ativo)
  const dmaGlobalCentro = env.reduce((s, dItem) => s + w(dItem) * (dmaCentroBico(dItem) || 0), 0);
  const dmaGlobalLimite = env.reduce((s, dItem) => s + w(dItem) * (dmaLimiteBico(dItem) || 0), 0);
  const dmaGlobalMisto  = env.reduce((s, dItem) => s + w(dItem) * (dmaMistoBico(dItem, iw, ow) || 0), 0);

 const dmaRefCentro = to2(env.reduce((s, d) => {
    const c = mid(d.params?.ideal_i, d.params?.ideal_f);
    return s + (Number.isFinite(c) ? w(d) * c : 0);
  }, 0));

  const maiorDesvio = analise
    .filter(a => Number.isFinite(a.dma))
    .sort((a, b) => b.dma - a.dma)[0];

  // Operador mais sobrecarregado (por número de bicos distintos)
  const opMaisSobrecarregado = Object
    .entries(opsStats)
    .map(([key, v]) => ({ operador: display(key), bicos: v.bicos.size, share: v.bicos.size / env.length }))
    .sort((a,b)=>(b.bicos - a.bicos) || (b.share - a.share))[0];

  // Desvios/violações para listas
  const desviosSpec = env
    .filter(d => d.media < d.params.min || d.media > d.params.max)
    .map(d => ({ bico: d.bico, media: d.media, min: d.params.min, max: d.params.max }));

  // operatorLoad no formato esperado pela UI
  const operatorLoad = Object.entries(opsStats).map(([key, v]) => ({
    operador: display(key),
    bicos: v.bicos.size,
    share: v.bicos.size / env.length
  })).filter(o =>
    o.bicos > sobrecarga.maxBicos || (o.share * 100) > sobrecarga.maxShare
  ).sort((a, b) => (b.bicos - a.bicos) || (b.share - a.share));

  // operatorIssues (simplificado: usa DMA médio ponderado por volume dos bicos do operador)
  const operatorIssues = Object.entries(opsStats).map(([op, v]) => {
    const bicosArr = Array.from(v.bicos);
    const registros = analise.filter(a => bicosArr.includes(String(a.bico)));

    const volOp = registros.reduce((s, r) => s + (r.total || 0), 0);
    const meanScore = volOp > 0
      ? to2(registros.reduce((s, r) => s + (r.dma * (r.total || 0)), 0) / volOp)
      : 0;

    const totalB = Math.max(registros.length, 1);
    const critPct = to1(100 * registros.filter(r => r.violacao).length / totalB);
    const atenPct = to1(100 * registros.filter(r => !r.violacao && r.drift).length / totalB);

    return { operador: display(op), meanScore, critPct, atenPct }; // << aqui
  }).filter(o => o.critPct > 0 || o.atenPct > 0)
    .sort((a, b) => (b.critPct - a.critPct) || (b.meanScore - a.meanScore));


  // risksTop já são os primeiros do ranking por score
  const risksTop = analise.slice(0, 5);

  // Datasets de gráficos (opcionais)
  const labelsBicos = env.map(d => String(d.bico).padStart(2, '0'));

  // mapa label -> extras p/ tooltip do gráfico de score
  const riskScoreExtraByLabel = analise.reduce((acc, a) => {
    const label = String(a.bico).padStart(2, '0');
    acc[label] = { breakdown: a.breakdown, media: a.media, total: a.total };
    return acc;
  }, {});

  // Ociosidade (prioriza dados de operadores; fallback para stats derivadas) ---
  let utilBase = [];

  const fmtOpRow = (o) =>
    (o?.matricula != null ? `${o.matricula} - ${o.nome}` : (o?.nome || '-'));

  // 1) Se veio a segunda consulta (operadores)
  if (operadoresInfo && operadoresInfo.length) {
    utilBase = operadoresInfo.map(o => {
      const leituras = Number(o.leituras ?? 0);
      const dias     = Number(o.dias_trabalhados ?? 0);
      const porDia   = dias > 0 ? (leituras / dias) : 0;
      return { operador: fmtOpRow(o), leituraPorDia: Number(porDia.toFixed(2)), dias };
    });
  } else {
    // 2) Fallback: derivar de opsStats (chave = matrícula)
    utilBase = Object.entries(opsStats || {}).map(([key, v]) => {
      const dias     = Number(v?.presencas ?? 0);
      const leituras = Number(v?.totalLeituras ?? 0);
      const porDia   = dias > 0 ? (leituras / dias) : 0;
      return { operador: display(key), leituraPorDia: Number(porDia.toFixed(2)), dias };
    });
  }

  // mediana
  const arr = utilBase.map(x => x.leituraPorDia).sort((a,b)=>a-b);
  const mediana = arr.length
    ? (arr.length % 2 ? arr[(arr.length-1)/2] : Number(((arr[arr.length/2-1]+arr[arr.length/2])/2).toFixed(2)))
    : 0;

  // filtros (estrito 50% e relaxado 80%)
  const filtra = (k) => utilBase
    .filter(x => mediana > 0 && x.leituraPorDia < k * mediana && x.dias >= 1)
    .sort((a,b)=>a.leituraPorDia - b.leituraPorDia);

  let underUtil = filtra(0.5);
  if (!underUtil.length) underUtil = filtra(0.8).slice(0, 3);

  // (opcional) ranking completo para debug/insumo futuro
  const utilRankAsc = [...utilBase].sort((a,b)=>a.leituraPorDia - b.leituraPorDia);

  const charts = {
    bicosMedia: {
      labels: labelsBicos,
      series: env.map(d => to2(Number(d.media)))
    },
    bicosStackedPct: {
      labels: labelsBicos,
      series: {
        abaixo: env.map(d => to1(pct(d.abaixo))),
        tolerancia: env.map(d => to1(pct(d.tolerancia))),
        ideais: env.map(d => to1(pct(d.ideais))),
        acima: env.map(d => to1(pct(d.acima)))
      }
    },
    riskScore: {
      labels: analise.map(a => String(a.bico).padStart(2, '0')),
      series: analise.map(a => a.score),
      extraByLabel: riskScoreExtraByLabel
    },
    operatorShare: (() => {
      const ops = Object.entries(opsStats)
      .map(([key, v]) => ({ op: key, share: to2(v.bicos.size / env.length) }))
      .filter(x => x.share > 0)
      .sort((a, b) => b.share - a.share);
    return {
      labels: ops.map(o => display(o.op)),
      series: ops.map(o => o.share)
    };
  })()
  };

  // ===== Saída =====
  const pesosFmt = Object.entries(pesos)
    .map(([k, v]) => `${PESOS_LABELS[k] || k}: ${v}`)
    .join(' · ');

  const dmaModeLabel =
   dmaCfg.mode === 'center' ? 'centro do ideal' :
   dmaCfg.mode === 'limit'  ? 'limite ideal mais próximo' :
                              `misto (${dmaCfg.insideWeight}× dentro / ${dmaCfg.outsideWeight}× fora)`;

  const summary = [
    `Bicos analisados: ${env.length}`,
    `Índice de conformidade: ${to1(conformidadeIdeal)}% ideal, ${to1(conformidadeTolerada)}% tolerância`,
    `Taxa de violações: ${to1(taxaViolacoes)}%`,
    `DMA (${dmaModeLabel}): ${to2(dmaGlobal)} g (centro ideal de referência: ${dmaRefCentro} g)`,
    `Pesos de pontuação — ${pesosFmt}`
  ];

  // Compatibilidade legada + novo formato
  return {
    // ----- legado -----
    resumo: `Foram analisados ${env.length} bicos.`,
    riscos: risksTop,
    sobrecarga: operatorLoad,
    desvios: desviosSpec,

    // ----- novo formato p/ Painel -----
    summary,
    kpis: {
      totalLeituras,                 
      conformidadeGeral: to1(conformidadeIdeal),
      conformidadeTolerada: to1(conformidadeTolerada),
      taxaViolacoes: to1(taxaViolacoes),
      dma: to2(dmaGlobal),            // <-- permanece NUMÉRICO
      dmaMode: dmaCfg.mode,
      dmaModeLabel,
      dmaRef: 'ideal_center',
      dmaRefValue: dmaRefCentro,
      maiorDesvio: maiorDesvio ? {
        bico: maiorDesvio.bico,
        dma: maiorDesvio.dma,
        media: to2(maiorDesvio.media),
        idealCentro: to2(mid(maiorDesvio.ideal.i, maiorDesvio.ideal.f) || NaN),
        operadores: maiorDesvio.operadores
      } : null,
      operadorMaisSobrecarregado: opMaisSobrecarregado || null
    },
      dma: {
      usado: {
        valor: to2(dmaGlobal),
        metodo: dmaCfg.mode,               // 'center' | 'limit' | 'mixed'
        metodoLabel: dmaModeLabel,         // ex.: 'misto (0.4× dentro / 1× fora)'
        pesos: { inside: iw, outside: ow },// só faz sentido no 'mixed'
        referencia: dmaRefCentro           // centro ideal ponderado (g)
      },
      centro: to2(dmaGlobalCentro),
      limite: to2(dmaGlobalLimite),
      misto:  to2(dmaGlobalMisto)
    },
    risksTop,
    operatorLoad,
    operatorIssues,
    operatorUnderUtilization: underUtil, // [{operador, leituraPorDia, dias}]
    charts,
    meta: { pesos, metas, sobrecarga, multiplicadorDias }
  };
}
